import React from 'react';
import { Link } from 'react-router-dom';
import Nav from './Nav';
import Footer from './Footer';

function LegalPage({ title, children }) {
  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 20px 80px' }}>
      <Link to="/" style={{ fontSize: 13, color: 'var(--muted)', textDecoration: 'none', display: 'inline-block', marginBottom: 32 }}>
        ← Back
      </Link>
      <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.3px', marginBottom: 6 }}>{title}</h1>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 40 }}>
        Last updated: June 24, 2026
      </p>
      <div style={{ color: 'var(--text)', lineHeight: 1.75, fontSize: 15 }}>
        {children}
      </div>
    </div>
  );
}

function H2({ children }) {
  return <h2 style={{ fontSize: 17, fontWeight: 700, margin: '36px 0 10px', letterSpacing: '-.1px' }}>{children}</h2>;
}
function P({ children }) {
  return <p style={{ margin: '0 0 16px', color: 'var(--muted)' }}>{children}</p>;
}
function UL({ children }) {
  return <ul style={{ margin: '0 0 16px', paddingLeft: 20, color: 'var(--muted)' }}>{children}</ul>;
}
function LI({ children }) {
  return <li style={{ marginBottom: 8 }}>{children}</li>;
}

function PrivacyPolicy({ currentUser, currentUserProfile }) {
  return (
    <>
      <Nav currentUser={currentUser} currentUserProfile={currentUserProfile} />
      <LegalPage title="Privacy Policy">
        <P>
          Your Top 8™ ("we," "us," or "our") is an independent service. This Privacy Policy
          describes what information we collect, how we use it, and your rights regarding that information.
          By using Your Top 8, you agree to this policy.
        </P>

        <H2>Information We Collect</H2>
        <P>We collect the following information when you use Your Top 8:</P>
        <UL>
          <LI><strong>Account information</strong> — your name, email address, and profile photo, provided through Google Sign-In or the email you sign up with.</LI>
          <LI><strong>Profile content</strong> — your chosen username, display name, bio, and any profile photo you upload.</LI>
          <LI><strong>Top 8 data</strong> — the people you add to your Top 8, including names, photos, and connection links (social profiles, phone numbers) that you enter manually.</LI>
          <LI><strong>Social links and phone</strong> — URLs to external social media profiles, and a phone number, that you choose to add to your profile. You set who can see each one.</LI>
          <LI><strong>Usage data</strong> — basic technical data such as your IP address, browser type, and pages visited, collected automatically by our hosting provider (Netlify) and backend (Google Firebase).</LI>
        </UL>

        <H2>How We Use Your Information</H2>
        <P>We use the information we collect to:</P>
        <UL>
          <LI>Provide and operate the Your Top 8 service.</LI>
          <LI>Display your public profile to other users.</LI>
          <LI>Send in-app notifications when relevant activity occurs (e.g., rank changes).</LI>
          <LI>Improve and maintain the service.</LI>
        </UL>
        <P>We do not sell your personal information to third parties. We do not use your information for advertising.</P>

        <H2>Information You Share Publicly</H2>
        <P>
          Your profile page — including your display name, username, profile photo, bio, and Top 8 — is
          public and visible to anyone with the link. Social links and a phone number you add to your
          own profile start private. They stay hidden until you choose who can see each one: your Top 8,
          your connections, or, for business accounts, the public. Private contact details you add to a
          person's tile, such as a phone number for a friend, are stored privately and are never shown
          on a public profile.
        </P>

        <H2>Third-Party Services</H2>
        <P>Your Top 8 uses the following third-party services that may process your data:</P>
        <UL>
          <LI><strong>Google Firebase</strong> — authentication, database (Firestore), and file storage. Subject to Google's Privacy Policy.</LI>
          <LI><strong>Netlify</strong> — web hosting and content delivery. Subject to Netlify's Privacy Policy.</LI>
          <LI><strong>Google Sign-In</strong> — used for account authentication. We receive only the profile information Google provides (name, email, photo).</LI>
        </UL>

        <H2>Data Retention</H2>
        <P>
          We retain your data for as long as your account is active. You may delete your account at any time by
          contacting us; upon deletion, your profile data will be removed from our systems within 30 days.
        </P>

        <H2>Children's Privacy</H2>
        <P>
          Your Top 8 is intended for adults and is not directed to anyone under the age of 18. We do not
          knowingly collect personal information from anyone under 18. If you believe someone under 18 has
          provided us with personal information, please contact us and we will remove it.
        </P>

        <H2>Your Rights</H2>
        <P>You have the right to:</P>
        <UL>
          <LI>Access the personal information we hold about you.</LI>
          <LI>Request correction of inaccurate data.</LI>
          <LI>Request deletion of your account and associated data.</LI>
          <LI>Withdraw consent for data processing where consent is the legal basis.</LI>
        </UL>
        <P>To exercise any of these rights, contact us at the email below.</P>

        <H2>Changes to This Policy</H2>
        <P>
          We may update this Privacy Policy from time to time. We will notify you of significant changes
          by updating the "Last updated" date at the top of this page. Continued use of Your Top 8 after
          changes constitutes acceptance of the updated policy.
        </P>

        <H2>Contact</H2>
        <P>
          Questions or concerns about this Privacy Policy?<br />
          Email us at <a href="mailto:privacy@yourtop8.com">privacy@yourtop8.com</a>
        </P>
      </LegalPage>
      <Footer />
    </>
  );
}

export default PrivacyPolicy;
